import React, { useEffect, useMemo } from "react";
import { Box, Button } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useMutation } from "@tanstack/react-query";
import { CSVLink } from "react-csv";
import MaterialReactTable from "material-react-table";
import dayjs from "dayjs";
import { SITE_EMISSIONS_COLUMNS_MAIN } from "./columnsMain";
import { CustomButton, CustomButtonGroup } from "./styles";



export const EmissionTableComponent = ({
  complementaryColumns = [],
  selected,
  setSelected,
  tableData,
}: {
  complementaryColumns?: any;
  selected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>,
  tableData: any;
}) => {
  const { mutateAsync: triggerRerender } = useMutation(() =>
    Promise.resolve(true)
  );

  // Triggering this mutation forces a re-render to fix an issue with MRT row virtualization 
  // when the top toolbar is hidden. Without it, rows may not render correctly on first load.
  useEffect(() => {
    triggerRerender();
  }, [triggerRerender]);

  const columns = useMemo(
    () => [...SITE_EMISSIONS_COLUMNS_MAIN, ...complementaryColumns],
    [complementaryColumns]
  );

  const CsvEmissionSummary = tableData?.map((item: any) => ({
    "Created Date": dayjs(item.createDate).format("YYYY-MM-DD"),
    "Start Date": item?.startDate,
    "End Date": item?.endDate,
    "Number of Days": item?.daysCovered,
    "Number of Unique Account/Meters": item?.numberOfUniqueCollectors,
    "Business Unit": item?.businessUnit,
    "Customer Site ID": item?.customerSiteId,
    Country: item.country,
    "Type of Site": item.sitetype,
    Site: item.sitename,
    "Utility Type": item.utilityName,
    Scope: item.scope,
    Source: item.source,
    Usage: item.usage,
    "Usage Unit": item.unit,
    "Renewable Energy Usage": item.recsUsage,
    "Renewable Energy Usage Unit": item.unit,
    "Standardized Usage": item.standardizedUsageValue,
    "Standardized Usage Unit": item.standardizedUsageUnit,
    "Location Renewable Usage": item.locationRenewableUsage,
    "Location Non Renewable Usage": item.locationNonRenewableUsage,
    "Market Renewable Usage": item.marketRenewableUsage,
    "Market Non Renewable Usage": item.marketNonRenewableUsage,
    Cost: item.cost,
    "Estimation Methodology": item?.estimationMethodology,
    Currency: item.currency,
    Emissions: item.co2Emission,
    "Emissions Unit": "tCO2eq",
    CO2: item.co2OnlyEmission,
    "CO2 Unit": "tCO2eq",
    "CH4-Fossil": item.ch4OnlyEmission,
    "CH4-Fossil Unit": "tCO2eq",

    "CH4 Non-Fossil": item.ch4NonFossilEmission,
    "CH4 Non_Fossil Unit": "tCO2eq",

    N2O: item.n2oOnlyEmission,
    "N2O Unit": "tCO2eq",

    "Emissions - Market": item.marketCo2Emission && `${item.marketCo2Emission}`,
    "Emissions - Market Unit": "tCO2eq",

    "CO2 - Market":
      item.marketCo2OnlyEmission && `${item.marketCo2OnlyEmission}`,
    "CO2 - Market Unit": "tCO2eq",

    "CH4 - Market":
      item.marketCh4OnlyEmission && `${item.marketCh4OnlyEmission}`,
    "CH4 - Market Unit": "tCO2eq",

    "CH4 Non-Fossil - Market":
      item.marketCh4NonFossilEmission && `${item.marketCh4NonFossilEmission}`,
    "CH4 Non-Fossil - Market Unit": "tCO2eq",

    "N2O - Market":
      item.marketN2oOnlyEmission && `${item.marketN2oOnlyEmission}`,
    "N2O - Market Unit": "tCO2eq",

    Area: item?.intensityArea && `${item.intensityArea}`,
    "Area Unit": item?.intensityArea && `${item.intensityAreaUnit}`,

    Production: item?.intensityProductUnit && `${item?.intensityProductUnit}`,
    "Production Unit":
      item?.intensityArea && `${item.unitsForIntensityProduct}`,

    Revenue: item?.intensityRevenue && `${item.intensityRevenue}`,
    "Revenue Currency": item?.intensityArea && `${item.intensityRevenueUnit}`,

    "Emissions Intensity by Area":
      item.emissionByIntensityArea && `${item.emissionByIntensityArea}`,
    "Emissions Intensity by Area Unit":
      item.emissionByIntensityArea && ` tCO2eq/${item.intensityAreaUnit}`,

    "Emissions Intensity by Production":
      item.emissionByIntensityProductUnit &&
      `${item.emissionByIntensityProductUnit}`,
    "Emissions Intensity by Production Unit":
      item.emissionByIntensityProductUnit &&
      ` tCO2eq/${item.unitsForIntensityProduct}`,

    "Emissions Intensity by Revenue":
      item.emissionByIntensityRevenue && `${item.emissionByIntensityRevenue}`,
    "Emissions Intensity by Revenue Unit":
      item.emissionByIntensityRevenue && ` tCO2eq/${item.intensityRevenueUnit}`,

    "Emissions Intensity by Area - Market":
      item.marketEmissionByIntensityArea &&
      `${item.marketEmissionByIntensityArea}`,
    "Emissions Intensity by Area - Market Unit":
      item.marketEmissionByIntensityArea && `tCO2eq/${item.intensityAreaUnit}`,

    "Emissions Intensity by Production - Market":
      item.marketEmissionByIntensityProductUnit &&
      `${item.marketEmissionByIntensityProductUnit}`,
    "Emissions Intensity by Production - Market Unit":
      item.marketEmissionByIntensityProductUnit &&
      `tCO2eq/${item.unitsForIntensityProduct}`,

    "Emissions Intensity by Revenue - Market":
      item.marketEmissionByIntensityRevenue &&
      `${item.marketEmissionByIntensityRevenue}`,
    "Emissions Intensity by Revenue - Market Unit":
      item.marketEmissionByIntensityRevenue &&
      `tCO2eq/${item.intensityRevenueUnit}`,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, width: "100%" }}>
      <Box sx={{ display: 'flex', gap: 5, justifyContent: 'space-between', alignItems: 'center' }}>
        <CustomButtonGroup>
          <CustomButton
            className={selected === 'Aggregated' ? 'Mui-selected' : ''}
            onClick={() => setSelected('Aggregated')}
          >
            Aggregated
          </CustomButton>
          <CustomButton
            className={selected === 'Monthly' ? 'Mui-selected' : ''}
            onClick={() => setSelected('Monthly')}
          >
            Monthly
          </CustomButton>
        </CustomButtonGroup>
        <CSVLink
          style={{
            textDecoration: "none",
            minWidth: "200px",
            height: "36px",
            borderRadius: "8px"
          }}
          data={CsvEmissionSummary}
          filename="Emission_Summary_Table.csv"
        >
          <Button sx={{ width: '200px', borderRadius: '8px', fontSize: '14px', fontWeight: "500" }} variant="contained" startIcon={<SendIcon />}>
            Export CSV
          </Button>
        </CSVLink>
      </Box>
      <MaterialReactTable
        muiTableContainerProps={{
          sx: {
            "& thead tr:first-of-type th:first-of-type": {
              paddingLeft: "20px",
            },
            "& tbody tr td:first-of-type": {
              paddingLeft: "20px",
            },
            "& tbody tr:first-of-type td > p": {
              display: "flex",
              width: "100%",
              justifyContent: "center",
            },
            height: "calc(100vh - 375px)",
            border: "1px solid #D9D9D9",
            borderRadius: "10px",
            width: "100%",
          },
        }}
        muiTablePaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "none",
          },
        }}
        muiTopToolbarProps={{
          sx: {
            display: "none",
          },
        }}
        muiBottomToolbarProps={{
          sx: {
            display: "none",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            lineHeight: "30px",
            "& .MuiBadge-root": {
              display: "none",
            },
          },
        }}
        muiTableHeadRowProps={{
          sx: {
            "& > th:first-of-type": {
              paddingLeft: "20px",
            },
            "& > th": {
              paddingTop: 1,
              paddingBottom: 1,
            },
            height: "auto",
            backgroundColor: "#F8FAFC",
          },
        }}
        columns={columns}
        data={tableData}
        enableColumnResizing
        enableDensityToggle={false}
        initialState={{ density: "compact" }}
        enablePagination={false}
        enableRowVirtualization
        enableGrouping
      />
    </Box>
  )
}
